import React, { useState, useRef, useCallback } from 'react';
import { getPetComponent } from './pets';
import { artIdFor } from './pets/ArtPet';
import { useViewMode } from '../context/ViewModeContext';
import { PetVisual, usePetHas3d } from './view/ModelVisual';
import { getCosmeticComponent, ATTACHMENT_POINTS } from './cosmetics';
import { useCosmeticSlots } from '../context/CatalogContext';
import './PetRenderer.css';

function intToRgb(tintInt) {
  if (!tintInt || tintInt === 0) return null;
  const r = (tintInt >> 16) & 0xFF;
  const g = (tintInt >> 8) & 0xFF;
  const b = tintInt & 0xFF;
  return `rgb(${r}, ${g}, ${b})`;
}

const MOOD_CLASSES = {
  happy: 'pet-bounce',
  sad: 'pet-droop',
  hungry: 'pet-slow',
  sleepy: 'pet-slow',
  energetic: 'pet-bounce',
  neutral: 'pet-idle',
};

export default function PetRenderer({
  petType = 'cat',
  variant,
  cosmetics = {},
  cosmeticPositions = {},
  tintColor: tintColorProp,
  mood = 'happy',
  size = 200,
  interactive = false,
  onExport,
}) {
  const catalogSlots = useCosmeticSlots();
  const [rotateY, setRotateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const lastXRef = useRef(0);
  const containerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    if (!interactive) return;
    setIsDragging(true);
    lastXRef.current = e.clientX;
  }, [interactive]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !interactive) return;
    const delta = e.clientX - lastXRef.current;
    setRotateY((prev) => Math.max(-30, Math.min(30, prev + delta * 0.5)));
    lastXRef.current = e.clientX;
  }, [isDragging, interactive]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (!interactive) return;
    setIsDragging(true);
    lastXRef.current = e.touches[0].clientX;
  }, [interactive]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !interactive) return;
    const delta = e.touches[0].clientX - lastXRef.current;
    setRotateY((prev) => Math.max(-30, Math.min(30, prev + delta * 0.5)));
    lastXRef.current = e.touches[0].clientX;
  }, [isDragging, interactive]);

  const handleExport = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const canvasSize = size * 2; // 2x for retina
    const canvas = document.createElement('canvas');
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    const ctx = canvas.getContext('2d');

    // Collect all positioned layers (cosmetics + pet base) with their layout info
    const layers = [];

    // Find the pet base: generated art is an <img>, older pets an <svg>
    const baseEl = el.querySelector('.pet-renderer-base img, .pet-renderer-base svg');
    if (baseEl) {
      const petSize = size * 0.85;
      layers.push({
        node: baseEl,
        x: (canvasSize - petSize * 2) / 2, // centered
        y: (canvasSize - petSize * 2) / 2,
        w: petSize * 2,
        h: petSize * 2,
        z: PET_Z,
      });
    }

    // Find cosmetic layers by their positioned parent divs
    const cosmeticDivs = el.querySelectorAll('.pet-renderer-cosmetic');
    cosmeticDivs.forEach((div) => {
      const svg = div.querySelector('img, svg');
      if (!svg) return;

      // Read position from inline style (left: X%, top: Y%)
      const leftPct = parseFloat(div.style.left) || 50;
      const topPct = parseFloat(div.style.top) || 50;
      const zIndex = parseInt(div.style.zIndex) || 0;

      // Get the rendered size of the SVG
      const svgRect = svg.getBoundingClientRect();
      const containerRect = el.getBoundingClientRect();
      const renderedW = (svgRect.width / containerRect.width) * canvasSize;
      const renderedH = (svgRect.height / containerRect.height) * canvasSize;

      // Position: percentage of canvas, centered (translate -50%, -50%)
      const cx = (leftPct / 100) * canvasSize;
      const cy = (topPct / 100) * canvasSize;

      // Parse rotation from transform
      const transformMatch = div.style.transform?.match(/rotate\((-?\d+)deg\)/);
      const rotation = transformMatch ? parseInt(transformMatch[1]) : 0;

      layers.push({
        node: svg,
        x: cx - renderedW / 2,
        y: cy - renderedH / 2,
        w: renderedW,
        h: renderedH,
        z: zIndex,
        rotation,
      });
    });

    // Sort by z-index so back items render first
    layers.sort((a, b) => a.z - b.z);

    // Render layers sequentially
    let idx = 0;
    const renderNext = () => {
      if (idx >= layers.length) {
        const link = document.createElement('a');
        link.download = `petling-${petType}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        if (onExport) onExport();
        return;
      }

      const layer = layers[idx];
      // Art is already an image on this origin; an inline SVG is serialised
      // into one first.
      const isImage = layer.node.tagName === 'IMG';
      let url = layer.node.src;
      if (!isImage) {
        const clone = layer.node.cloneNode(true);
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        const data = new XMLSerializer().serializeToString(clone);
        url = URL.createObjectURL(new Blob([data], { type: 'image/svg+xml;charset=utf-8' }));
      }

      const img = new Image();
      img.onload = () => {
        ctx.save();
        if (layer.rotation) {
          // Rotate around the center of the cosmetic
          const cx = layer.x + layer.w / 2;
          const cy = layer.y + layer.h / 2;
          ctx.translate(cx, cy);
          ctx.rotate((layer.rotation * Math.PI) / 180);
          ctx.drawImage(img, -layer.w / 2, -layer.h / 2, layer.w, layer.h);
        } else {
          ctx.drawImage(img, layer.x, layer.y, layer.w, layer.h);
        }
        ctx.restore();
        if (!isImage) URL.revokeObjectURL(url);
        idx++;
        renderNext();
      };
      img.onerror = () => {
        if (!isImage) URL.revokeObjectURL(url);
        idx++;
        renderNext();
      };
      img.src = url;
    };

    renderNext();
  }, [size, petType, onExport]);

  const PetComponent = getPetComponent(petType);
  const { mode } = useViewMode();
  // In 3D the pet is its model, turning; the 2D cosmetics are placed on a
  // picture and cannot sit on it, so they wait for 2D.
  const has3d = usePetHas3d(petType);
  const show3d = mode === '3d' && has3d === true;
  const attachments = ATTACHMENT_POINTS[petType] || ATTACHMENT_POINTS.default;
  const moodClass = MOOD_CLASSES[mood] || 'pet-idle';

  // Extract tint from cosmetics or prop
  const rawTint = tintColorProp || cosmetics.tint_color || cosmetics.TINT_COLOR || null;
  const resolvedTint = rawTint && rawTint !== '0' ? intToRgb(parseInt(rawTint, 10)) : null;

  // Pet renders at z-index 10, same as editor
  const PET_Z = 10;

  // Collect active cosmetics (skip tint entries)
  const cosmeticLayers = [];
  Object.entries(cosmetics).forEach(([slot, cosmeticId]) => {
    if (!cosmeticId) return;
    if (slot === 'tint_color' || slot === 'TINT_COLOR') return;
    const CosmeticComp = getCosmeticComponent(cosmeticId);
    if (!CosmeticComp) return;

    const actualSlot = catalogSlots[cosmeticId] || slot;
    const defaultPoint = attachments[actualSlot] || attachments.head;

    // Use custom position if saved, otherwise default attachment point
    const custom = cosmeticPositions[cosmeticId];
    const point = custom && custom.x != null ? { x: custom.x, y: custom.y } : defaultPoint;
    const rotation = custom?.rotation || 0;
    const scale = custom?.scale || 1.0;
    const defaultDepth = actualSlot === 'back' ? 5 : 15;
    const depth = custom?.depth ?? defaultDepth;
    const cosmeticSize = size * 0.25 * scale;

    cosmeticLayers.push(
      <div
        key={cosmeticId}
        className="pet-renderer-cosmetic"
        style={{
          position: 'absolute',
          left: `${point.x}%`,
          top: `${point.y}%`,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
          zIndex: depth,
        }}
      >
        <CosmeticComp size={cosmeticSize} />
      </div>
    );
  });

  return (
    <div
      ref={containerRef}
      className={`pet-renderer ${interactive ? 'pet-renderer-interactive' : ''}`}
      style={{
        width: size,
        height: size,
        perspective: interactive ? '800px' : 'none',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUp}
    >
      <div
        className="pet-renderer-inner"
        style={{
          transform: interactive ? `rotateY(${rotateY}deg)` : 'none',
          width: '100%',
          height: '100%',
          position: 'relative',
        }}
      >
        {/* All cosmetic layers - depth determines front/back relative to pet */}
        {!show3d && cosmeticLayers}

        {/* Pet base at fixed z-index */}
        <div className="pet-renderer-base" style={{ zIndex: PET_Z }}>
          {show3d ? (
            <PetVisual petId={petType} size={size * 0.85} live={size >= 140} />
          ) : (
            <PetComponent
              size={size * 0.85}
              mood={mood}
              wearsCosmetics={cosmeticLayers.length > 0}
              color={resolvedTint || (variant ? getVariantColor(petType, variant) : undefined)}
              className={moodClass}
            />
          )}
          {/* Art pets tint themselves, masked to their outline. */}
          {resolvedTint && !show3d && !artIdFor(petType) && <div className="pet-renderer-tint-overlay" style={{ background: resolvedTint }} />}
        </div>
      </div>

      {interactive && (
        <button className="pet-renderer-export btn btn-ghost" onClick={handleExport} title="Save as PNG">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2V10M8 10L5 7M8 10L11 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 12V13C2 13.55 2.45 14 3 14H13C13.55 14 14 13.55 14 13V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

function getVariantColor(petType, variant) {
  const VARIANT_COLORS = {
    cat: { black: '#2C2C2C', orange: '#D4762A', white: '#F0F0F0', gray: '#8B8B8B', siamese: '#C8B090', calico: '#D4762A', tuxedo: '#3A3A3A' },
    wolf: { gray: '#8B8B8B', white: '#E8E8E8', brown: '#8B6940', black: '#3A3A3A' },
    fox: { red: '#D4762A', arctic: '#E8E8E8', silver: '#9E9E9E' },
    slime: { crystal: '#81D4FA', fire: '#FF7043', ice: '#B3E5FC', nature: '#81C784', shadow: '#616161' },
    baby_dragon: { purple: '#7E57C2', red: '#E53935', blue: '#42A5F5', green: '#66BB6A', gold: '#FFB300' },
  };
  return VARIANT_COLORS[petType]?.[variant] || undefined;
}
